import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { nextTick } from "vue";
import { useTitle } from "@vueuse/core";
import MainLayout from "@/layouts/MainLayout.vue";
import { some, compact } from "underscore";
import { ROUTE_NAME, SERVICE_NAME } from "./routes.constants";
import { useMenuStore } from "@/pinia/useMenuStore";
import { storeToRefs } from "pinia";
import useEnv from "@/composables/useEnv";

const { isDev } = useEnv();

const routes: Array<RouteRecordRaw> = compact([
  {
    path: "/",
    component: MainLayout,
    redirect: {
      name: ROUTE_NAME.COMPOSE,
    },
    children: [
      {
        path: "groups",
        redirect: {
          name: ROUTE_NAME.SMART_GROUPS,
        },
        children: [
          {
            path: "",
            name: ROUTE_NAME.SMART_GROUPS,
            component: () => import("@/views/groups/groups.vue"),
            meta: {
              title: "SmartGroups",
              service: SERVICE_NAME.SMART_GROUPS,
            },
          },
          {
            path: ":id",
            name: ROUTE_NAME.SMART_GROUP_MEMBER,
            component: () =>
              import(
                "@/views/groups/smart-group-members/smart-group-member-details.vue"
              ),
            meta: {
              title: "SmartGroup Members",
              service: SERVICE_NAME.SMART_GROUPS,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.SMART_GROUPS,
                  text: "SmartGroups",
                },
                {
                  name: ROUTE_NAME.SMART_GROUP_MEMBER,
                  text: "SmartGroup Members",
                },
              ],
            },
          },
        ],
      },
      {
        path: "templates",
        redirect: {
          name: ROUTE_NAME.TEMPLATES,
        },
        children: [
          {
            path: "",
            name: ROUTE_NAME.TEMPLATES,
            component: () =>
              import("@/views/templates/template-details/templates.vue"),
            meta: {
              title: "Templates",
              service: SERVICE_NAME.TEMPLATES,
            },
          },
          {
            path: ":templateId",
            name: ROUTE_NAME.EDIT_TEMPLATE,
            component: () =>
              import("@/views/templates/edit-template/add-edit-template.vue"),
            meta: {
              title: "Edit Template",
              service: SERVICE_NAME.TEMPLATES,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.TEMPLATES,
                  text: "Templates",
                },
                {
                  name: ROUTE_NAME.EDIT_TEMPLATE,
                  text: "Edit Template",
                },
              ],
            },
          },
          {
            path: ":templateId",
            name: ROUTE_NAME.EDIT_VIEWED_TEMPLATE,
            component: () =>
              import("@/views/templates/edit-template/add-edit-template.vue"),
            meta: {
              title: "Edit Template",
              service: SERVICE_NAME.TEMPLATES,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.TEMPLATES,
                  text: "Templates",
                },
                {
                  name: ROUTE_NAME.TEMPLATE_DETAILS,
                  text: "Template Details",
                },
                {
                  name: ROUTE_NAME.EDIT_TEMPLATE,
                  text: "Edit Template",
                },
              ],
            },
          },
          {
            path: "add",
            name: ROUTE_NAME.ADD_TEMPLATE,
            component: () =>
              import("@/views/templates/edit-template/add-edit-template.vue"),
            meta: {
              title: "Add Template",
              service: SERVICE_NAME.TEMPLATES,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.TEMPLATES,
                  text: "Templates",
                },
                {
                  name: ROUTE_NAME.ADD_TEMPLATE,
                  text: "Add Template",
                },
              ],
            },
          },
          {
            path: "template-details/:templateId",
            name: ROUTE_NAME.TEMPLATE_DETAILS,
            component: () =>
              import("@/views/templates/view-template-details.vue"),
            meta: {
              title: "Template Details",
              service: SERVICE_NAME.TEMPLATES,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.TEMPLATES,
                  text: "Templates",
                },
                {
                  name: ROUTE_NAME.TEMPLATE_DETAILS,
                  text: "Template Details",
                },
              ],
            },
          },
        ],
      },
      {
        path: "account-management",
        name: ROUTE_NAME.ACCOUNT_MANAGEMENT,
        component: () =>
          import("@/views/account-management/account-management.vue"),
        meta: {
          title: "Account Management",
          service: SERVICE_NAME.ACCOUNT_MANAGEMENT,
        },
      },
      {
        path: "contacts",
        redirect: {
          name: ROUTE_NAME.CONTACTS,
        },
        children: [
          {
            path: "",
            name: ROUTE_NAME.CONTACTS,
            component: () =>
              import(
                /* webpackChunkName: "contacts" */ "@/views/contacts/contacts.vue"
              ),
            meta: {
              title: "Contacts",
              service: SERVICE_NAME.CONTACTS,
              // intro: IntroGuideRoute.CONTACTS,
            },
          },
          {
            path: "contact-filter/:contactFilterId",
            name: ROUTE_NAME.EDIT_CONTACT_FILTER_MEMBERS,
            props: true,
            component: () =>
              import(
                "@/views/contacts/contact-filter/edit-contact-filter-members.vue"
              ),
            meta: {
              title: "Edit FilterGroup Members",
              service: SERVICE_NAME.CONTACTS,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.CONTACTS,
                  text: "FilterGroup",
                },
                {
                  name: ROUTE_NAME.EDIT_CONTACT_FILTER_MEMBERS,
                  text: "Filter Members",
                },
              ],
            },
          },
          {
            path: "contact-filter/:contactFilterId/edit-contact-filter",
            name: ROUTE_NAME.EDIT_CONTACT_FILTER,
            props: true,
            component: () =>
              import("@/views/contacts/contact-filter/edit-contact-filter.vue"),
            meta: {
              title: "Edit FilterGroup",
              service: SERVICE_NAME.CONTACTS,
              // intro: IntroGuideRoute.CONTACT_FILTER_EDIT,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.CONTACTS,
                  text: "FilterGroup",
                },
                {
                  name: ROUTE_NAME.EDIT_CONTACT_FILTER_MEMBERS,
                  text: "Filter Members",
                },
                {
                  name: ROUTE_NAME.EDIT_CONTACT_FILTER,
                  text: "Builder",
                },
              ],
            },
          },
          {
            path: "contact-filter/add-contact-filter",
            name: ROUTE_NAME.ADD_CONTACT_FILTER,
            component: () =>
              import("@/views/contacts/contact-filter/add-contact-filter.vue"),
            meta: {
              title: "Add FilterGroup",
              service: SERVICE_NAME.CONTACTS,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.CONTACTS,
                  text: "FilterGroup",
                },
                {
                  text: "Builder",
                },
              ],
            },
          },
          {
            path: "contact-group/:contactGroupId",
            name: ROUTE_NAME.CONTACT_GROUP_MEMBERS,
            component: () =>
              import(
                "@/views/contacts/contact-groups/contact-group-members/contact-group-members.vue"
              ),
            meta: {
              title: "ContactGroup Members",
              service: SERVICE_NAME.CONTACTS,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.CONTACTS,
                  text: "Contacts",
                },
                {
                  name: ROUTE_NAME.CONTACT_GROUP_MEMBERS,
                  text: "ContactGroups",
                },
              ],
            },
          },
          {
            path: "contact-group/:contactGroupId/add-members",
            name: ROUTE_NAME.ADD_CONTACT_GROUP_MEMBERS,
            component: () =>
              import(
                "@/views/contacts/contact-groups/add-members/add-contact-group-members.vue"
              ),
            meta: {
              title: "Add ContactGroup Members",
              service: SERVICE_NAME.CONTACTS,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.CONTACTS,
                  text: "Contacts",
                },
                {
                  name: ROUTE_NAME.CONTACT_GROUP_MEMBERS,
                  text: "ContactGroups",
                },
                {
                  name: ROUTE_NAME.ADD_CONTACT_GROUP_MEMBERS,
                  text: "Add Members",
                },
              ],
            },
          },
        ],
      },
      {
        path: "diary/schedule-message/:eventId?",
        name: ROUTE_NAME.SCHEDULE_MESSAGE,
        component: () =>
          import("@/views/diary/schedule-message/schedule-message.vue"),
        meta: {
          title: "Schedule Message",
          service: SERVICE_NAME.DIARY,
          breadCrumbs: [
            {
              name: ROUTE_NAME.DIARY,
              text: "Diary",
            },
            {
              name: ROUTE_NAME.SCHEDULE_MESSAGE,
              text: "Schedule Message",
            },
          ],
        },
      },
      {
        path: "queue-manager",
        redirect: {
          name: ROUTE_NAME.QUEUE_MANAGER,
        },
        children: [
          {
            path: "",
            name: ROUTE_NAME.QUEUE_MANAGER,
            component: () =>
              import("@/views/queue-manager/bulk-job/bulk-job.vue"),
            meta: {
              title: "Bulk Manager",
              service: SERVICE_NAME.QUEUE_MANAGER,
            },
          },
          {
            path: "job-details/:jobId",
            name: ROUTE_NAME.BULK_JOB_DETAILS,
            component: () =>
              import(
                "@/views/queue-manager/transactions-details/transaction-details.vue"
              ),
            meta: {
              title: "Bulk Manager Job Details",
              service: ROUTE_NAME.QUEUE_MANAGER,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.QUEUE_MANAGER,
                  text: "Bulk Manager",
                },
                {
                  name: ROUTE_NAME.BULK_JOB_DETAILS,
                  text: "Job Details",
                },
              ],
            },
          },
          {
            path: "bulk-job",
            name: ROUTE_NAME.BULK_COMPOSE,
            component: () => import("@/views/bulk-compose/bulk-compose.vue"),
            meta: {
              title: "Bulk Job",
              service: SERVICE_NAME.QUEUE_MANAGER,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.QUEUE_MANAGER,
                  text: "Bulk Manager",
                },
                {
                  name: ROUTE_NAME.BULK_COMPOSE,
                  text: "Bulk Job",
                },
              ],
            },
          },
        ],
      },
      {
        path: "diverts",
        redirect: {
          name: ROUTE_NAME.DIVERTS,
        },
        children: [
          {
            path: "",
            name: ROUTE_NAME.DIVERTS,
            component: () => import("@/views/diverts/diverts.vue"),
            meta: {
              title: "Diverts",
              service: SERVICE_NAME.DIVERTS,
            },
          },
          {
            path: "divert-profiles/:id",
            name: ROUTE_NAME.DIVERT_PROFILE_MEMBERS,
            component: () =>
              import("@/views/diverts/profile-members/profile-members.vue"),
            meta: {
              title: "Divert Profile Members",
              service: SERVICE_NAME.DIVERTS,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.DIVERTS,
                  text: "Profiles",
                },
                {
                  name: ROUTE_NAME.DIVERT_PROFILE_MEMBERS,
                  text: "Divert Profile Members",
                },
              ],
            },
          },
          {
            path: "add-divert/:type",
            name: ROUTE_NAME.ADD_DIVERT,
            component: () => import("@/views/diverts/devices/devices.vue"),
            meta: {
              title: "Add Divert",
              service: SERVICE_NAME.DIVERTS,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.DIVERTS,
                  text: "Diverts",
                },
                {
                  name: ROUTE_NAME.ADD_DIVERT,
                  text: "Add Divert",
                },
              ],
            },
          },
        ],
      },
      {
        path: "quick-send",
        name: ROUTE_NAME.QUICK_SEND,
        component: () => import("@/views/quick-actions/actions.vue"),
        meta: {
          title: "Quick Send",
          service: ROUTE_NAME.QUICK_SEND,
        },
      },
      {
        path: "campaign-manager-147",
        redirect: {
          name: ROUTE_NAME.CAMPAIGN_MANAGER,
        },
        children: [
          {
            path: "",
            name: ROUTE_NAME.CAMPAIGN_MANAGER,
            component: () =>
              import("@/views/campaign-manager/campaign-manager.vue"),
            meta: {
              title: "Campaign Manager",
              service: SERVICE_NAME.CAMPAIGN_MANAGER,
            },
          },
          {
            path: "subscribers/:campaignId",
            name: ROUTE_NAME.CAMPAIGN_SUBSCRIBERS,
            props: true,
            component: () =>
              import("@/views/campaign-manager/campaign-subscribers/index.vue"),
            meta: {
              title: "Campaign Manager",
              service: SERVICE_NAME.CAMPAIGN_MANAGER,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.CAMPAIGN_MANAGER,
                  text: "Campaign Manager",
                },
                {
                  name: ROUTE_NAME.CAMPAIGN_SUBSCRIBERS,
                  text: "Subscribers",
                },
              ],
            },
          },
        ],
      },
      {
        path: "messages",
        redirect: {
          name: ROUTE_NAME.MESSAGES,
        },
        children: [
          {
            path: "",
            name: ROUTE_NAME.MESSAGES,
            component: () =>
              import("@/views/message-logs/messages-ui/layout.vue"),
            meta: {
              title: "Messages",
              service: SERVICE_NAME.MESSAGES,
            },
          },
          {
            path: ":campaignId",
            name: ROUTE_NAME.CAMPAIGN_MESSAGES,
            component: () =>
              import("@/views/message-logs/messages-ui/layout.vue"),
            meta: {
              title: "Messages",
              service: SERVICE_NAME.MESSAGES,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.CAMPAIGN_MANAGER,
                  text: "Campaign Manager",
                },
                {
                  name: ROUTE_NAME.CAMPAIGN_SUBSCRIBERS,
                  text: "Subscribers",
                },
                {
                  name: ROUTE_NAME.MESSAGES,
                  text: "Messages",
                },
              ],
            },
          },
          {
            path: "charts",
            name: ROUTE_NAME.MESSAGES_CHARTS,
            component: () => import("@/views/message-logs/charts/index.vue"),
            meta: {
              title: "Messages",
              service: SERVICE_NAME.MESSAGES,
            },
          },
          {
            path: "members",
            name: ROUTE_NAME.MESSAGE_MEMBERS,
            component: () =>
              import(
                "@/views/message-logs/transaction-details/members-detail-page.vue"
              ),
            meta: {
              title: "Members",
              service: SERVICE_NAME.MESSAGES,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.MESSAGES,
                  text: "Messages",
                },
                {
                  name: ROUTE_NAME.MESSAGE_MEMBERS,
                  text: "Members",
                },
              ],
            },
            props: (route: any) => ({
              ...route.params,
            }),
          },
          {
            path: "exports",
            name: ROUTE_NAME.MESSAGE_EXPORTS,
            component: () =>
              import("@/views/message-logs/components/exports.vue"),
            meta: {
              title: "Exports",
              service: SERVICE_NAME.MESSAGES,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.MESSAGES,
                  text: "Messages",
                },
                {
                  name: ROUTE_NAME.MESSAGE_EXPORTS,
                  text: "Exports",
                },
              ],
            },
          },
          {
            path: "members/:groupId",
            name: ROUTE_NAME.MESSAGE_NESTED_MEMBER,
            component: () =>
              import(
                "@/views/message-logs/transaction-details/members-detail-page.vue"
              ),
            meta: {
              title: "Nested Members",
              service: SERVICE_NAME.MESSAGES,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.MESSAGES,
                  text: "Messages",
                },
                {
                  name: ROUTE_NAME.MESSAGE_MEMBERS,
                  text: "Members",
                },
                {
                  name: ROUTE_NAME.MESSAGE_NESTED_MEMBER,
                  showParams: true,
                },
              ],
            },
          },
          {
            path: "details/members",
            name: ROUTE_NAME.MESSAGE_MEMBER_DETAILS,
            component: () =>
              import("@/views/message-logs/logs-view/members-detail-page.vue"),
            meta: {
              title: "Details",
              service: SERVICE_NAME.MESSAGES,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.MESSAGES,
                  text: "Messages",
                },
                {
                  name: ROUTE_NAME.MESSAGE_MEMBER_DETAILS,
                  text: "Details",
                },
              ],
            },
            props: (route: any) => ({
              ...route.params,
            }),
          },
        ],
      },
      {
        path: "app-management",
        name: ROUTE_NAME.APP_MANAGEMENT,
        component: () => import("@/views/app-management/app-management.vue"),
        meta: {
          title: "App Management",
          service: SERVICE_NAME.APP_MANAGEMENT,
        },
      },
      {
        path: "pager-events",
        redirect: {
          name: ROUTE_NAME.PAGER_EVENTS,
        },
        children: [
          {
            path: "",
            name: ROUTE_NAME.PAGER_EVENTS,
            component: () => import("@/views/pager-events/pager-events.vue"),
            meta: {
              title: "Pager Events",
              service: SERVICE_NAME.PAGER_EVENTS,
            },
          },
          {
            path: "history",
            name: ROUTE_NAME.PAGER_EVENTS_HISTORY,
            component: () =>
              import("@/views/pager-events/pager-events-history.vue"),
            meta: {
              title: "Pager Event History",
              service: SERVICE_NAME.PAGER_EVENTS,
              breadCrumbs: [
                {
                  name: ROUTE_NAME.PAGER_EVENTS,
                  text: "Pager Events",
                },
                {
                  name: ROUTE_NAME.PAGER_EVENTS_HISTORY,
                  text: "Pager Event History",
                },
              ],
            },
          },
        ],
      },
      {
        path: "/not-found",
        name: ROUTE_NAME.NOT_FOUND,
        meta: {
          title: "Not Found",
        },
        component: () => import("@/views/404.vue"),
      },
    ],
  },
  {
    path: "/message-compose",
    redirect: {
      name: ROUTE_NAME.COMPOSE,
    },
    children: [
      {
        path: "",
        name: ROUTE_NAME.COMPOSE,
        component: () =>
          import(
            /* webpackChunkName: "message-compose" */ "@/views/message-compose/message-compose.vue"
          ),
        meta: {
          title: "Message Compose",
          service: SERVICE_NAME.MESSAGES,
        },
        props: true,
      },
      {
        path: "edit",
        name: ROUTE_NAME.EDIT_COMPOSE,
        component: () =>
          import(
            /* webpackChunkName: "message-compose" */ "@/views/message-compose/message-compose.vue"
          ),
        meta: {
          title: "Message Compose",
          service: ROUTE_NAME.MESSAGES,
          breadCrumbs: [
            {
              name: ROUTE_NAME.QUICK_SEND,
              text: "Quick Send",
            },
            {
              name: ROUTE_NAME.EDIT_COMPOSE,
              text: "Message Compose",
            },
          ],
        },
      },
      {
        path: "createQA",
        name: ROUTE_NAME.CREATE_QUICK_ACTIONS,
        component: () =>
          import(
            /* webpackChunkName: "message-compose" */ "@/views/message-compose/message-compose.vue"
          ),
        meta: {
          title: "Message Compose",
          service: SERVICE_NAME.MESSAGES,
          breadCrumbs: [
            {
              name: ROUTE_NAME.QUICK_SEND,
              text: "Quick Send",
            },
            {
              name: ROUTE_NAME.CREATE_QUICK_ACTIONS,
              text: "Message Compose",
            },
          ],
        },
        props: true,
      },
      {
        path: "campaign/:campaignId",
        name: ROUTE_NAME.SEND_MESSAGE_TO_CAMPAIGN,
        component: () =>
          import(
            /* webpackChunkName: "message-compose" */ "@/views/message-compose/message-compose.vue"
          ),
        meta: {
          title: "Message Compose",
          service: ROUTE_NAME.MESSAGES,
          breadCrumbs: [
            {
              name: ROUTE_NAME.CAMPAIGN_MANAGER,
              text: "Campaign Manager",
            },
          ],
        },
      },
      {
        path: "campaign/:campaignId/subscriber/:subscriberNumber",
        name: ROUTE_NAME.SEND_MESSAGE_TO_SUBSCRIBERS,
        component: () =>
          import(
            /* webpackChunkName: "message-compose" */ "@/views/message-compose/message-compose.vue"
          ),
        meta: {
          title: "Message Compose",
          service: ROUTE_NAME.MESSAGES,
          breadCrumbs: [
            {
              name: ROUTE_NAME.CAMPAIGN_MANAGER,
              text: "Campaign Manager",
            },
            {
              name: ROUTE_NAME.CAMPAIGN_SUBSCRIBERS,
              text: "Campaign Subscribers",
            },
          ],
        },
      },
    ],
  },
  {
    path: "/diary",
    name: ROUTE_NAME.DIARY,
    component: () =>
      import(/* webpackChunkName: "diary" */ "@/views/diary/diary.vue"),
    meta: {
      title: "Diary",
      service: SERVICE_NAME.DIARY,
    },
  },
  {
    path: "/chats9878",
    name: ROUTE_NAME.CONVERSATIONS,
    component: () => import("@/views/conversations/conversations.vue"),
    meta: {
      title: "Messages",
      service: SERVICE_NAME.MESSAGES,
    },
  },
  {
    path: "/settings",
    name: ROUTE_NAME.SETTINGS,
    component: () => import("@/views/settings/settings.vue"),
    meta: {
      title: "Settings",
      service: SERVICE_NAME.SETTINGS,
    },
  },
  {
    path: "/messages_new",
    name: ROUTE_NAME.MESSAGES_NEW,
    children: [
      {
        path: "",
        component: () => import("@/views/message-logs/messages_new/layout.vue"),
        meta: {
          title: "Messages",
          service: SERVICE_NAME.MESSAGES,
        },
      },
      {
        path: "/members_new",
        name: ROUTE_NAME.MESSAGE_MEMBERS_NEW,
        component: () =>
          import(
            "@/views/message-logs/messages_new/transaction-details/members-detail-page.vue"
          ),
        meta: {
          title: "Members",
          service: SERVICE_NAME.MESSAGES,
          breadCrumbs: [
            {
              name: ROUTE_NAME.MESSAGES_NEW,
              text: "Messages",
            },
            {
              name: ROUTE_NAME.MESSAGE_MEMBERS_NEW,
              text: "Members",
            },
          ],
        },
        props: (route: any) => ({
          ...route.params,
        }),
      },
    ],
  },
  isDev && {
    path: "/components/:name",
    name: "components",
    component: () => import("@/views/playground/index.vue"),
    meta: {
      title: "Components",
      service: SERVICE_NAME.CONTACTS,
    },
  },
]);

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeResolve(async (to, _from: any, next) => {
  const { loadMenuItems } = useMenuStore();
  const { getMenuItems } = storeToRefs(useMenuStore());
  await loadMenuItems();
  if (to.name !== ROUTE_NAME.NOT_FOUND) {
    const isRouteExists = some(
      getMenuItems.value,
      (menu: any) => menu.service === to.meta?.service,
    );
    if (getMenuItems.value?.length && !isRouteExists) {
      next({ name: ROUTE_NAME.NOT_FOUND });
    }
  }
  next();
});

router.beforeEach((_to, from: any, next) => {
  localStorage.setItem("LS_ROUTE_KEY", from.name);
  next();
});

router.afterEach(async (to) => {
  // Use next tick to handle router history correctly
  // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
  await nextTick(() => {
    useTitle(`${to.meta.title}` || "Critico Connect");
  });
  const { setMobileMenuDisplay } = useMenuStore();
  setMobileMenuDisplay(false);
});

export default router;
